import { getConsent } from "./consent";

let loaded = false;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/**
 * Dynamically inject the Google Tag Manager script — only when:
 *   1. The user has consented (localStorage), AND
 *   2. VITE_GTM_ID is set at build time.
 *
 * Called on app boot (for returning visitors) and after the cookie banner's
 * Accept button (for first-time consenters). Idempotent — safe to call twice.
 */
export function loadGtmIfConsented(): void {
  if (loaded) return;
  if (getConsent() !== "granted") return;

  const id = import.meta.env.VITE_GTM_ID as string | undefined;
  if (!id) return;

  loaded = true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(id)}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

/** Push an event to GTM's dataLayer. No-op if GTM hasn't loaded yet. */
export function track(event: string, params?: Record<string, unknown>): void {
  if (!window.dataLayer) return;
  window.dataLayer.push({ event, ...params });
}
